import { FormEvent, useState } from 'react'
import { creatorPostsAPI } from '../services/api'
import type { CreatorPost } from '../types'
import { apiErrorMessage } from '../utils/apiError'

const NETWORK_NAMES: Record<CreatorPost['network'], string> = {
  x: 'X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
}
const COUNTS = ['views', 'likes', 'comments', 'shares'] as const
type Count = (typeof COUNTS)[number]

const fmt = (n: number) => n.toLocaleString()

/** "1,200 views · 85 likes", leaving out counts nobody has (never shown as 0). */
export const countsLine = (post: CreatorPost): string =>
  COUNTS.filter((c) => post[c] !== null)
    .map((c) => `${fmt(post[c] as number)} ${c}`)
    .join(' · ')

interface PostedLinksProps {
  posts: CreatorPost[]
  onChange: (post: CreatorPost) => void
  onRemove: (id: number) => void
}

/** Where a draft was posted by the creator, and how each post is doing. */
const PostedLinks = ({ posts, onChange, onRemove }: PostedLinksProps) => {
  const [editing, setEditing] = useState<number | null>(null)
  const [draft, setDraft] = useState<Record<Count, string>>({ views: '', likes: '', comments: '', shares: '' })
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (posts.length === 0) return null

  const run = async (id: number, job: () => Promise<void>, failed: string) => {
    setBusy(id)
    setError(null)
    try {
      await job()
    } catch (e) {
      setError(apiErrorMessage(e, failed))
    } finally {
      setBusy(null)
    }
  }

  const startEditing = (post: CreatorPost) => {
    setEditing(post.id)
    setDraft({
      views: post.views?.toString() ?? '',
      likes: post.likes?.toString() ?? '',
      comments: post.comments?.toString() ?? '',
      shares: post.shares?.toString() ?? '',
    })
  }

  const saveCounts = (e: FormEvent, post: CreatorPost) => {
    e.preventDefault()
    const counts = Object.fromEntries(
      COUNTS.filter((c) => draft[c].trim() !== '').map((c) => [c, Number(draft[c])])
    ) as Partial<Record<Count, number>>
    run(post.id, async () => {
      onChange(await creatorPostsAPI.setCounts(post.id, counts))
      setEditing(null)
    }, "Couldn't save the counts.")
  }

  return (
    <div className="mb-4 rounded-md border border-gray-200 p-3" data-testid="posted-links">
      <h4 className="text-sm font-medium text-gray-900">Where you posted it</h4>
      <ul className="mt-2 space-y-2">
        {posts.map((post) => (
          <li key={post.id} className="text-sm" data-testid="posted-link">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <a href={post.url} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline">
                {NETWORK_NAMES[post.network]}
              </a>
              <span className="text-gray-700" data-testid="post-counts">
                {countsLine(post) || 'No counts yet'}
              </span>
              <span className="text-xs text-gray-500">
                {post.stats_source === 'manual' ? 'typed in' : post.stats_source ? `from ${NETWORK_NAMES[post.network]}` : ''}
              </span>
              <span className="ml-auto flex gap-2 text-xs">
                {post.auto_stats ? (
                  <button
                    type="button"
                    disabled={busy === post.id}
                    onClick={() => run(post.id, async () => onChange(await creatorPostsAPI.refresh(post.id)), "Couldn't read the counts.")}
                    className="text-primary-600 hover:underline disabled:opacity-50"
                  >
                    Update counts
                  </button>
                ) : (
                  <button type="button" onClick={() => startEditing(post)} className="text-primary-600 hover:underline">
                    Enter counts
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy === post.id}
                  onClick={() => run(post.id, async () => {
                    await creatorPostsAPI.remove(post.id)
                    onRemove(post.id)
                  }, "Couldn't remove the link.")}
                  className="text-gray-500 hover:underline disabled:opacity-50"
                  aria-label={`Remove the ${NETWORK_NAMES[post.network]} link`}
                >
                  Remove
                </button>
              </span>
            </div>
            {post.stats_error && <p className="text-xs text-gray-500">{post.stats_error}</p>}
            {editing === post.id && (
              <form onSubmit={(e) => saveCounts(e, post)} className="mt-2 flex flex-wrap items-end gap-2">
                {COUNTS.map((c) => (
                  <label key={c} className="text-xs text-gray-600">
                    <span className="block capitalize">{c}</span>
                    <input
                      type="number"
                      min={0}
                      value={draft[c]}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [c]: e.target.value }))}
                      className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm"
                    />
                  </label>
                ))}
                <button
                  type="submit"
                  disabled={busy === post.id}
                  className="rounded-md bg-primary-600 px-3 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Save counts
                </button>
                <button type="button" onClick={() => setEditing(null)} className="px-2 py-1 text-xs text-gray-600">
                  Cancel
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}
    </div>
  )
}

export default PostedLinks
